// Shared sort/filter bar used across Lead Score, Contacts/CRM, and Call Tool so
// "score them → open my list → sort by warm, by industry" is one coherent flow,
// not three different implementations (Phase 8 item 6).

export interface ContactFilters {
  sort: string;
  tier: string;        // '' | Hot | Warm | Cold
  category: string;    // '' | industry tag
  researched: string;  // '' | yes | no
  workStatus: string;  // '' | new | contacted | followup | done
  stage: string;       // '' | pipeline stage id
}

export const DEFAULT_FILTERS: ContactFilters = { sort: 'recent', tier: '', category: '', researched: '', workStatus: '', stage: '' };

const SORTS_DEFAULT = [
  { v: 'recent', label: 'Most recent' },
  { v: 'score', label: 'Score (high→low)' },
  { v: 'name', label: 'Name (A→Z)' },
  { v: 'added', label: 'Newest added' },
];

function Chips({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { v: string; label: string }[] }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => (
          <button key={o.v} onClick={() => onChange(o.v)}
            className={`rounded-md border px-2 py-0.5 text-xs font-semibold transition ${value === o.v ? 'border-power bg-power/15 text-electric' : 'border-line text-muted hover:text-soft'}`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ContactControls({
  value, onChange, categories = [], stages = [], sorts = SORTS_DEFAULT, show = {},
}: {
  value: ContactFilters;
  onChange: (next: ContactFilters) => void;
  categories?: string[];
  stages?: { id: string; name: string }[];
  sorts?: { v: string; label: string }[];
  show?: { tier?: boolean; category?: boolean; researched?: boolean; status?: boolean; stage?: boolean };
}) {
  const s = { tier: true, category: true, researched: true, status: true, stage: true, ...show };
  const set = (patch: Partial<ContactFilters>) => onChange({ ...value, ...patch });

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-line bg-surface/50 px-3 py-2.5">
      <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
        Sort
        <select className="input py-1 text-sm" value={value.sort} onChange={(e) => set({ sort: e.target.value })}>
          {sorts.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
      </label>

      {s.tier && <Chips label="Tier" value={value.tier} onChange={(v) => set({ tier: v })} options={[{ v: '', label: 'All' }, { v: 'Hot', label: 'Hot' }, { v: 'Warm', label: 'Warm' }, { v: 'Cold', label: 'Cold' }]} />}

      {s.category && categories.length > 0 && (
        <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
          Industry
          <select className="input py-1 text-sm" value={value.category} onChange={(e) => set({ category: e.target.value })}>
            <option value="">All</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      )}

      {s.stage && stages.length > 0 && (
        <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
          Stage
          <select className="input py-1 text-sm" value={value.stage} onChange={(e) => set({ stage: e.target.value })}>
            <option value="">All</option>
            {stages.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
          </select>
        </label>
      )}

      {s.researched && <Chips label="Research" value={value.researched} onChange={(v) => set({ researched: v })} options={[{ v: '', label: 'All' }, { v: 'yes', label: 'Researched' }, { v: 'no', label: 'Not yet' }]} />}

      {s.status && <Chips label="Status" value={value.workStatus} onChange={(v) => set({ workStatus: v })} options={[{ v: '', label: 'All' }, { v: 'new', label: 'New' }, { v: 'contacted', label: 'Contacted' }, { v: 'followup', label: 'Follow-up' }, { v: 'done', label: 'Done' }]} />}
    </div>
  );
}

// Build a query string from filters (used by every list fetch).
export function filtersToQuery(f: ContactFilters): string {
  const p = new URLSearchParams();
  if (f.sort) p.set('sort', f.sort);
  if (f.tier) p.set('tier', f.tier);
  if (f.category) p.set('category', f.category);
  if (f.researched) p.set('researched', f.researched);
  if (f.workStatus) p.set('workStatus', f.workStatus);
  if (f.stage) p.set('stage', f.stage);
  return p.toString();
}
